/**
 * @module estrutura/estruturaediting
 */

import { Plugin } from 'ckeditor5/src/core';
import { Element } from 'ckeditor5/src/engine';
import EstruturaCommand from './commands/estruturacommand';
import { Estrutura } from './models';

const ESTRUTURA = 'estrutura';

/**
 * The estrutura editing.
 *
 * Introduces the `'estrutura'` command and the `'estrutura'` model element.
 *
 * @extends {Plugin}
 */
export default class EstruturaEditing extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'EstruturaEditing';
	}

	/**
	 * @inheritDoc
	 */
	constructor( editor ) {
		super( editor );

		/**
		 * @type {Map<number, Element>}
		 */
		this._estruturas = new Map();

		/**
		 * @type {Map<number, number>}
		 */
		this._estruturasIds = new Map();
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;
		editor.commands.add( ESTRUTURA, new EstruturaCommand( editor ) );

		this._defineSchema();
		this._defineConverters();
		this._definePostFixer();
		this._defineEvents();
	}

	afterInit() {
		const editor = this.editor;
		editor.once( 'ready', () => {
			editor.conversion.for( 'editingDowncast' ).add( getEstruturaSelectionChange( editor ) )
		}, {
			priority: 'lowest'
		} );
	}

	/**
	 * @private
	 */
	_defineSchema() {
		const schema = this.editor.model.schema;
		schema.register( ESTRUTURA, {
			allowIn: '$root',
			allowAttributes: [ ESTRUTURA ],
			allowContentOf: '$root'
		} );

		// Disallow estrutura in estrutura.
		schema.addChildCheck( ( ctx, childDef ) => {
			if ( ctx.endsWith( '$root' ) && childDef.name != ESTRUTURA ) {
				return false;
			}
			if ( !ctx.endsWith( '$root' ) && childDef.name == ESTRUTURA ) {
				return false;
			}
		} );
	}

	/**
	 * @private
	 */
	_defineConverters() {
		const conversion = this.editor.conversion;

		conversion.elementToElement( {
			model: ESTRUTURA,
			view: ESTRUTURA
		} );

		conversion.for( 'upcast' )
			.attributeToAttribute( {
				view: {
					name: ESTRUTURA,
					key: 'data'
				},
				model: {
					name: ESTRUTURA,
					key: ESTRUTURA,
					value: viewElement => {
						const infoEncodedStr = viewElement.getAttribute( 'data' );
						let info = null;
						if ( infoEncodedStr ) {
							info = JSON.parse( atob( infoEncodedStr ) );
						}
						return new Estrutura( info );
					}
				}
			} );

		conversion.for( 'dataDowncast' )
			.attributeToAttribute( {
				model: {
					name: ESTRUTURA,
					key: ESTRUTURA
				},
				view: ( modelAttributeValue ) => ( {
					key: 'data',
					value: modelAttributeValue ? btoa( JSON.stringify( modelAttributeValue ) ) : null
				} )
			} );
	}

	/**
	 * @private
	 */
	_definePostFixer() {
		const doc = this.editor.model.document;
		const idGenerator = ( function* () {
			var index = 0;
			while ( index < index + 1 )
				yield index++;
		} )();

		doc.registerPostFixer( writer => {
			const changes = doc.differ.getChanges();

			let wasFixed = false;

			// Check if the changes lead to an empty root in the editor.
			for ( const entry of changes ) {
				if ( entry.type == 'insert' && entry.name == ESTRUTURA ) {
					const item = entry.position.nodeAfter;
					if ( item.isEmpty ) {
						const paragraph = writer.createElement( 'paragraph' );
						writer.insert( paragraph, item, 'end' );
						wasFixed = true;
					} else if ( !this.editor.model.schema.checkChild( entry.position, item ) ) {
						writer.unwrap( item );
						wasFixed = true;
					}

					const estData = item.getAttribute( ESTRUTURA );
					if ( estData.id == null ) {
						estData.id = idGenerator.next().value;
						wasFixed = true;
					}
				}
			}

			return wasFixed;
		} );
	}

	/**
	 * @private
	 */
	_defineEvents() {
		const editor = this.editor;
		const doc = editor.model.document;

		doc.on( `update:${ ESTRUTURA }`, ( evt, estrutura ) => {
			if ( !estrutura ) {
				console.error( "Tentando atualizar estrutura com objeto nulo." );
				return;
			}

			let id = estrutura.id;
			if ( id == null ) {
				id = this._estruturasIds.get( estrutura.codigo );
			}
			if ( id == null ) {
				console.error( "Tentando atualizar '%o' sem id de mapeamento.", estrutura );
				return;
			}

			const estruturaModel = this._estruturas.get( id );

			let attribute = new Estrutura( estrutura );
			let content = null;
			const disp = editor.plugins.get( 'EstruturaLabelEditing' )._dispositivos.get( estrutura.dispositivo );
			if ( disp && disp.reseta ) {
				content = editor.data.parse( [ estrutura ] );

				const oldEst = estruturaModel.toJSON();
				const newEst = content.getChild( 0 ).toJSON();

				// Se o texto for o mesmo, nao precisa alterar nada
				if ( JSON.stringify( oldEst.children ) === JSON.stringify( newEst.children ) ) {
					content = null;

					// Se inclusive as propriedades da estrutura forem iguais, nada a fazer
					if ( JSON.stringify( oldEst.attributes ) === JSON.stringify( newEst.attributes ) ) {
						attribute = null;
					}
				}
			}

			if ( content ) {
				editor.model.change( writer => {
					const selection = writer.model.createSelection( writer.model.document.selection );

					const range = writer.createRangeOn( estruturaModel );
					writer.model.insertContent( content, range );
					for ( const selRange of selection.getRanges() ) {
						if ( range.isIntersecting( selRange ) ) {
							return
						}
					}
					writer.setSelection( selection );
				} );
			} else if ( attribute ) {
				const newEst = new Estrutura( estrutura );
				editor.model.change( writer => {
					writer.setAttribute( ESTRUTURA, newEst, estruturaModel );
				} );
			}
		}, {
			priority: 'low'
		} );

		doc.on( 'change:data', () => {
			const changes = doc.differ.getChanges( {
				includeChangesInGraveyard: true
			} );

			for ( const entry of changes ) {
				if ( entry.type == 'insert' && entry.name == ESTRUTURA ) {
					const item = entry.position.nodeAfter;
					const estData = item.getAttribute( ESTRUTURA );

					if ( entry.position.root.rootName == '$graveyard' ) {
						this._estruturas.delete( estData.id );
						if ( estData.codigo != null ) {
							this._estruturasIds.delete( estData.codigo );
						}
					} else {
						this._estruturas.set( estData.id, item );
						if ( estData.codigo != null ) {
							this._estruturasIds.set( estData.codigo, estData.id );
						}
					}
				} else if ( entry.type == 'attribute' && entry.attributeKey == ESTRUTURA ) {
					const estNewData = entry.attributeNewValue;
					if ( estNewData ) {
						if ( estNewData.codigo != null && estNewData.id != null ) {
							this._estruturasIds.set( estNewData.codigo, estNewData.id );
						}
					}
				}
			}
		} );
	}
}

function getEstruturaSelectionChange( editor ) {
	return dispatcher => {
		let selectedEstruturas = null;
		let selectedPositionStart = null;
		let selectedPositionEnd = null;
		dispatcher.on( 'selection', ( evt, data, conversionApi ) => {
			const newEstruturas = [];
			const minMax = Array.from( data.selection.getSelectedBlocks() )
				.map( t => t.getPath()[ 0 ] )
				.reduce( ( acc, val ) => {
					acc[ 0 ] = ( acc[ 0 ] === undefined || val < acc[ 0 ] ) ? val : acc[ 0 ]
					acc[ 1 ] = ( acc[ 1 ] === undefined || val > acc[ 1 ] ) ? val : acc[ 1 ]
					return acc;
				}, [] );

			for ( let i = minMax[ 0 ]; i <= minMax[ 1 ]; i++ ) {
				newEstruturas.push( data.selection.anchor.root.getNodeByPath( [ i ] ).getAttribute( ESTRUTURA ) );
			}

			if ( minMax[ 0 ] !== selectedPositionStart || minMax[ 1 ] !== selectedPositionEnd ) {
				const evtData = {
					oldSelection: selectedEstruturas,
					newSelection: newEstruturas
				};
				selectedEstruturas = newEstruturas;
				selectedPositionStart = minMax[ 0 ];
				selectedPositionEnd = minMax[ 1 ];

				editor.model.document.fire( `selection:${ ESTRUTURA }`, evtData );
			}
		} );
	}
}
